import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  Stack,
  Chip,
  useTheme,
  alpha,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Speed,
  Support,
  Security,
  Assessment,
} from '@mui/icons-material';

const SLAPage = () => {
  const theme = useTheme();

  const slaMetrics = [
    {
      icon: <Speed />,
      title: 'Download Availability',
      value: '99.5%',
      description: 'Website and software download services',
      details: 'Excluding planned maintenance and third-party outages'
    },
    {
      icon: <Support />,
      title: 'Community Response',
      value: 'Best Effort',
      description: 'Community-driven support via GitHub',
      details: 'No guaranteed response times for community edition'
    },
    {
      icon: <Security />,
      title: 'Security Updates',
      value: '14 days',
      description: 'Critical security patches release target',
      details: 'Best effort timeline for open source releases'
    },
    {
      icon: <Assessment />,
      title: 'Issue Resolution',
      value: 'Variable',
      description: 'Resolution depends on community contribution',
      details: 'Open source model - no guaranteed timelines'
    },
  ];

  const supportTiers = [
    {
      name: 'Community Edition',
      level: 'Open Source',
      response: 'No guarantee',
      availability: 'Community-driven',
      channels: ['GitHub Issues', 'GitHub Discussions', 'Community Wiki'],
      coverage: ['Bug reports', 'Feature requests', 'General questions', 'Community help']
    },
    {
      name: 'Enterprise Support',
      level: 'Custom Agreement',
      response: 'Negotiable',
      availability: 'Per agreement',
      channels: ['Direct contact', 'Priority support', 'Custom channels'],
      coverage: ['Dedicated support', 'Custom features', 'Integration assistance', 'Training']
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box
        sx={{
          py: { xs: 6, md: 10 },
          background: alpha(theme.palette.background.default, 0.5),
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h1"
              sx={{
                fontWeight: 800,
                mb: 3,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Service Level Agreement
            </Typography>
            <Typography
              variant="h5"
              color="text.secondary"
              sx={{ 
                mb: 4, 
                maxWidth: 800, 
                mx: 'auto',
                lineHeight: 1.6,
              }}
            >
              Our commitment to service quality, reliability, and support standards
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        {/* SLA Metrics */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" sx={{ mb: 4, fontWeight: 700, textAlign: 'center' }}>
            Service Level Commitments
          </Typography>
          <Grid container spacing={4}>
            {slaMetrics.map((metric, index) => (
              <Grid size={{ xs: 12, md: 6 }} key={index}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 4,
                    height: '100%',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box
                      sx={{
                        mr: 2,
                        p: 1.5,
                        borderRadius: 2,
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        color: 'primary.main',
                      }}
                    >
                      {metric.icon}
                    </Box>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                        {metric.value}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {metric.title}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                    {metric.description}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {metric.details}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Support Tiers */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" sx={{ mb: 4, fontWeight: 700, textAlign: 'center' }}>
            Support Tiers
          </Typography>
          <Grid container spacing={4}>
            {supportTiers.map((tier, index) => (
              <Grid size={{ xs: 12, md: 6 }} key={index}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 4,
                    height: '100%',
                    border: '2px solid',
                    borderColor: index === 1 ? 'primary.main' : 'divider',
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  {index === 1 && (
                    <Chip
                      label="Premium"
                      color="primary"
                      sx={{
                        position: 'absolute',
                        top: -12,
                        right: 20,
                        fontWeight: 600,
                      }}
                    />
                  )}
                  <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
                    {tier.name}
                  </Typography>
                  <Typography variant="h6" color="primary" sx={{ mb: 3, fontWeight: 600 }}>
                    {tier.level}
                  </Typography>
                  
                  <Stack spacing={2} sx={{ mb: 3 }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Response Time:
                      </Typography>
                      <Typography variant="body2">{tier.response}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Availability:
                      </Typography>
                      <Typography variant="body2">{tier.availability}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Support Channels:
                      </Typography>
                      {tier.channels.map((channel, idx) => (
                        <Typography key={idx} variant="body2">• {channel}</Typography>
                      ))}
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Coverage:
                      </Typography>
                      {tier.coverage.map((item, idx) => (
                        <Typography key={idx} variant="body2">• {item}</Typography>
                      ))}
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Detailed Terms */}
        <Paper
          elevation={1}
          sx={{
            p: { xs: 4, md: 6 },
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h4" sx={{ mb: 4, fontWeight: 700 }}>
            Service Level Agreement Terms
          </Typography>

          <Stack spacing={4}>
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                1. Software Distribution
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                PWGen-Rust is distributed as downloadable software that runs locally on user devices. Our service commitments focus on:
              </Typography>
              <Typography variant="body2" component="ul" sx={{ pl: 3 }}>
                <li>Website and documentation availability (best effort 99.5% uptime)</li>
                <li>Software download availability via GitHub releases</li>
                <li>Source code availability and version control</li>
                <li>Community support infrastructure (GitHub Issues, Discussions)</li>
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                2. Support Model
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                PWGen-Rust follows an open source support model:
              </Typography>
              <Typography variant="body2" component="ul" sx={{ pl: 3 }}>
                <li><strong>Community Edition:</strong> No guaranteed support - community-driven assistance via GitHub</li>
                <li><strong>Enterprise Agreements:</strong> Custom support terms negotiated per contract</li>
                <li><strong>Security Issues:</strong> Reported via security@pwgen.dev for responsible disclosure</li>
                <li><strong>Bug Reports:</strong> Tracked publicly via GitHub Issues with community triage</li>
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                3. Security and Updates
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                As locally-installed software, PWGen-Rust security depends on both our releases and user responsibility:
              </Typography>
              <Typography variant="body2" component="ul" sx={{ pl: 3 }}>
                <li>Critical security vulnerabilities addressed within 14 days (best effort)</li>
                <li>Security advisories published when issues are discovered</li>
                <li>Users responsible for updating their installed software</li>
                <li>No automatic updates - users control when to upgrade</li>
                <li>Backward compatibility maintained when possible</li>
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                4. Limitations and User Responsibility
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                As locally-installed software, users are responsible for:
              </Typography>
              <Typography variant="body2" component="ul" sx={{ pl: 3 }}>
                <li>Installing and maintaining PWGen-Rust on their systems</li>
                <li>Backing up their password vault data</li>
                <li>Ensuring system compatibility and security</li>
                <li>Understanding that data never leaves their device</li>
                <li>Updating software when security patches are released</li>
                <li>Following security best practices for master passwords</li>
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                5. Enterprise Agreements and Custom Terms
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                For organizations requiring formal support commitments:
              </Typography>
              <Typography variant="body2" component="ul" sx={{ pl: 3 }}>
                <li>Custom Service Level Agreements available upon request</li>
                <li>Negotiable response times and support channels</li>
                <li>Custom feature development and integration support</li>
                <li>Training and implementation assistance</li>
                <li>Dedicated support contacts and escalation procedures</li>
                <li>Contact enterprise@pwgen.dev for custom arrangements</li>
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                6. Contact and Support Channels
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Support varies by user type and agreement:
              </Typography>
              <Typography variant="body2" component="ul" sx={{ pl: 3 }}>
                <li><strong>Community Users:</strong> GitHub Issues and Discussions (community-driven)</li>
                <li><strong>Security Reports:</strong> security@pwgen.dev (responsible disclosure)</li>
                <li><strong>Enterprise Inquiries:</strong> enterprise@pwgen.dev (custom agreements)</li>
                <li><strong>General Questions:</strong> GitHub Discussions or community wiki</li>
                <li><strong>Custom SLA Requests:</strong> enterprise@pwgen.dev</li>
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ mt: 6, p: 3, backgroundColor: alpha(theme.palette.info.main, 0.1), borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              <strong>Last Updated:</strong> January 2025 | 
              <strong> Version:</strong> 1.0 | 
              <strong> Effective Date:</strong> January 1, 2025
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default SLAPage;